import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api, patch, post } from "../lib/api";
import { useCan } from "../lib/session";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../components/ui";
import { Due, PriorityBadge, StatusPill } from "../components/work";

interface Rule { id: string; name: string; description: string; eventType: string; source: string | null; condition: string | null; action: { type: string; [k: string]: unknown }; active: boolean }

export function IntegrationsPage() {
  const can = useCan();
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: ["rules"], queryFn: () => api<{ items: Rule[] }>("/api/connectors/rules") });
  const events = useQuery({ queryKey: ["inbound"], queryFn: () => api<{ items: Array<{ id: string; source: string; eventId: string; type: string; receivedAt: string; results: Array<Record<string, unknown>> }> }>("/api/connectors/events"), refetchInterval: 30_000 });
  const callbacks = useQuery({ queryKey: ["callbacks"], queryFn: () => api<{ items: Array<{ id: string; targetService: string; path: string; createdAt: string; sentAt: string | null; attempts: number; lastStatus: number | null; lastError: string | null; parked: boolean }> }>("/api/connectors/callbacks"), refetchInterval: 30_000 });
  const toggle = useMutation({ mutationFn: (r: Rule) => patch(`/api/connectors/rules/${r.id}`, { active: !r.active }), onSuccess: () => void qc.invalidateQueries({ queryKey: ["rules"] }) });
  const retry = useMutation({ mutationFn: (id: string) => post(`/api/connectors/callbacks/${id}/retry`, {}), onSuccess: () => void qc.invalidateQueries({ queryKey: ["callbacks"] }) });
  return (
    <div className="space-y-4">
      <PageHeader title="Integrations" subtitle="How the other apps connect to ONE, and what they have sent." />
      <ErrorNote error={toggle.error ?? retry.error} />
      <Card title="Event rules">
        <ul className="-mx-1 divide-y divide-[var(--line)]">
          {rules.data?.items.map((r) => (
            <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 px-1 py-3 text-sm first:pt-0 last:pb-0">
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{r.name}</span>
                  {r.active ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />On</span> : <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-500 ring-1 ring-inset ring-zinc-200"><span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />Off</span>}
                </span>
                <span className="muted mt-1 block text-[0.8125rem]">{r.description}</span>
                <span className="subtle mt-1 block font-mono text-xs">{r.source ? `${r.source} → ` : ""}{r.eventType} ⇒ {r.action.type}{r.action.workflowKey ? ` (${String(r.action.workflowKey)})` : r.action.spaceKey ? ` (${String(r.action.spaceKey)})` : ""}</span>
              </span>
              {can("events.outbox.manage") && <button className={r.active ? "btn btn-sm" : "btn btn-primary btn-sm"} onClick={() => toggle.mutate(r)}>{r.active ? "Turn off" : "Turn on"}</button>}
            </li>
          ))}
        </ul>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Received events">
          {events.data?.items.length ? (
            <ul className="space-y-2 text-xs">
              {events.data.items.map((e) => (
                <li key={e.id}><span className="font-mono">{e.type}</span> <span className="muted">from {e.source} · {fmtDate(e.receivedAt)}</span>
                  <span className="block">{e.results.map((r, i) => <span key={i} className={r.error ? "text-red-600" : "text-emerald-600"}>{String(r.rule)}: {String(r.error ?? r.workflow ?? r.workItem ?? r.notified ?? "ok")} </span>)}{e.results.length === 0 && <span className="muted">no rule matched</span>}</span>
                </li>
              ))}
            </ul>
          ) : <Empty>No events received yet.</Empty>}
        </Card>
        <Card title="Status sent back to apps">
          {callbacks.data?.items.length ? (
            <ul className="space-y-2 text-xs">
              {callbacks.data.items.map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-2">
                  <span><span className="font-mono">{c.targetService}{c.path}</span> <span className="muted">· {fmtDate(c.createdAt)} · {c.attempts} tries</span>
                    {c.sentAt ? <span className="block text-emerald-600">delivered {fmtDate(c.sentAt)}</span> : <span className="block text-amber-600">{c.parked ? "parked" : "pending"}{c.lastStatus ? ` (${c.lastStatus})` : ""} {c.lastError}</span>}
                  </span>
                  {!c.sentAt && can("events.outbox.manage") && <button className="btn btn-sm" onClick={() => retry.mutate(c.id)}>Retry</button>}
                </li>
              ))}
            </ul>
          ) : <Empty>Nothing sent yet.</Empty>}
        </Card>
      </div>
    </div>
  );
}

interface ExternalItem { system: string; externalId: string; ref: string | null; title: string; status: string; state: string; priority: string; url: string | null; site: string | null; dueAt: string | null; assigneeNames: string[] }
const SYSTEM_LABEL: Record<string, string> = { fusiontask: "FusionTask", "mythos-tasks": "Mythos Tasks", "mythos-ticketing": "Ticketing" };
const SYSTEM_TONE: Record<string, string> = { fusiontask: "bg-brand-50 text-brand-700 ring-brand-100", "mythos-tasks": "bg-teal-50 text-teal-700 ring-teal-200", "mythos-ticketing": "bg-amber-50 text-amber-800 ring-amber-200" };

export function useExternal(view: "mine" | "all") {
  return useQuery({ queryKey: ["external", view], queryFn: () => api<{ items: ExternalItem[] }>(`/api/work/external?view=${view}`) });
}

export function ExternalList({ items }: { items: ExternalItem[] }) {
  return (
    <ul className="-mx-2">
      {items.map((i) => (
        <li key={`${i.system}:${i.externalId}`}>
          <a href={i.url ?? "#"} target="_blank" rel="noreferrer" className="group flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-[#f4f5fb]">
            <ExternalLink size={16} className="subtle mt-1 shrink-0 group-hover:text-brand-600" />
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2"><span className="text-[0.9375rem] font-semibold group-hover:text-brand-700">{i.title}</span><PriorityBadge p={i.priority} /></span>
              <span className="muted mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold ring-1 ring-inset ${SYSTEM_TONE[i.system] ?? "bg-slate-100 text-slate-700 ring-slate-200"}`}>{SYSTEM_LABEL[i.system] ?? i.system}</span>{i.ref && <span>{i.ref}</span>}<StatusPill state={i.state} label={i.status} /><Due at={i.dueAt} />{i.site && <span>{i.site}</span>}{i.assigneeNames.length > 0 && <span>{i.assigneeNames.join(", ")}</span>}
              </span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}
